import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import UserSummary from '@/models/Settings/Users/UserSummary';
import UserDetails from '@/models/Settings/Users/UserDetails';
import Role from '@/models/Settings/Roles/Role';
import { AppModule } from '@/store/Auth/AppModule'
import CreateUserRequest from '@/models/Settings/Users/CreateUserRequest';
import UpdateUserRequest from '@/models/Settings/Users/UpdateUserRequest';
import UpdateUserRolesRequest from '@/models/Settings/Users/UpdateUserRolesRequest'
import PermissionUtils from '@/lib/permissionUtils';

export interface IUsersState {
    loading: boolean;
    loadingDialog: boolean;
    users: UserSummary[];
    totalUsersPages: number;
    user: UserDetails;
    newUserId: string;
    pageSize: number;
}

@Module({ dynamic: true, store, name: 'UsersModule', namespaced: true })
class UsersStore extends VuexModule implements IUsersState {

    public loading: boolean = true;
    public loadingDialog: boolean = false;
    public users: UserSummary[] = [];
    public totalUsersPages: number = 1;
    public user: UserDetails = new UserDetails();
    public newUserId: string = null;
    public pageSize: number = PermissionUtils.getPageSize();

    @Action
    async createUser(request: CreateUserRequest) {

        this.setLoadingDialog(true);
        this.context.commit("setUserFromCreate", null);

        await ajax.post(`/portal-backend/fi-api/internal/users`, request)
        .then(response => {
            this.context.commit("setUserFromCreate", response.data.id);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async editUserDetails(request: UpdateUserRequest) {

        this.setLoadingDialog(true);

        await ajax.patch(`/portal-backend/fi-api/internal/users/${this.user.id}`, request)
        .then(response => {
            this.context.commit("setUser", response.data);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async editUserRoles(request: UpdateUserRolesRequest) {

        this.setLoadingDialog(true);

        await ajax.put(`/portal-backend/fi-api/internal/users/${this.user.id}/roles`, request)
        .then(() => {
            // Note: We don't get any data back in the repsonse here so don't set anything or we
            // will lose data, instead the UI will redirect back to the main users page
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async getUsers() {

        this.setLoading(true);

        await ajax.get('/portal-backend/fi-api/internal/users')
        .then(response => {
            this.context.commit("setUsers", response.data.results);
            // There are no "records" in the response in this case, so we have to calculate the length of results manually
            this.context.commit("setUsersTotalPages", response.data.results.length);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async getUser(id: string) {

        this.setLoading(true);

        await ajax.get(`/portal-backend/fi-api/internal/users/${id}`)
        .then(response => {
            this.context.commit("setUser", response.data);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async getRolesForUser(id: string) {

        this.setLoading(true);

        await ajax.get(`/portal-backend/fi-api/internal/users/${id}/roles`)
        .then(response => {
            this.context.commit("setRolesForUser", response.data.results);
            this.context.commit("setGlobalAdminForUser", response.data.globalAdmin);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    cleanState() {
        this.context.commit("resetState");
    }

    @Mutation
    resetState() {
        this.loading = true;
        this.loadingDialog = false;
        this.user = new UserDetails();
        this.totalUsersPages = 1;
        this.users = [];
        this.newUserId = null;
    }

    @Mutation
    private async setLoading(loading: boolean) {
        this.loading = loading;
    }

    @Mutation
    private async setLoadingDialog(loading: boolean) {
        this.loadingDialog = loading;
    }

    @Mutation
    private async setUsers(response: UserSummary[]) {
        if (response) {
            this.users = response;
        }
    }

    @Mutation
    private async setRolesForUser(response: Role[]) {
        if (response) {
            this.user.roles = response;
        }
    }

    @Mutation
    private async setGlobalAdminForUser(response: boolean) {
        if (response) {
            this.user.globalAdmin = response;
        }
    }

    @Mutation
    private async setUsersTotalPages(total: number) {
        this.totalUsersPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setUser(response: UserDetails) {
        this.user = response;
    }

    @Mutation
    private async setUserFromCreate(userId: string) {
        this.newUserId = userId;
    }

}

export const UsersModule = getModule(UsersStore);